import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import useSWR from "swr";
import Section from "./components/body-main-elements/Section";
import TopSection from "./components/page-structure/TopSection";
import Cookies from 'js-cookie';
import CurrencyPopup from "./components/popups/CurrencyPopup";
import CryptemberHeader from "./components/page-structure/CryptemberHeader";
import CryptemberFooter from "./components/page-structure/CryptemberFooter";
import { library } from '@fortawesome/fontawesome-svg-core'
import { fab } from '@fortawesome/free-brands-svg-icons'
import { ORIGIN, KEY } from "./utils/configs.js";
import {
  faCoffee,
  faCog,
  faSpinner,
  faQuoteLeft,
  faSquare,
  faCheckSquare,
  faArrowUpLong,
  faArrowUp
} from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    getSortedSections
} from "./utils/utils";
import { isAssessmentStar } from "./assessments/assessmentView";
import SectionsConfigurationModal from "./components/popups/SectionsConfigurationModal";
import HistoryTab from "./history/HistoryTab";
import MyTradesTab from "./trades/MyTradesTab";
import MacroEventsTab from "./macro/MacroEventsTab";
import SentimentTab from "./sentiment/SentimentTab";
import SetupsTab, { isFuturesSetup } from "./setups/SetupsTab";
import DiscoveryWatchlistTab from "./watchlist/DiscoveryWatchlistTab";
import { getUpcomingMacroCount } from "./macro/macroEventUtils";
import { FiActivity, FiChevronsDown, FiChevronsUp } from "react-icons/fi";
import AdminPanel, { useAdminSession } from "./admin/AdminPanel";
import { adminApi } from "./admin/adminApi";
import { appModalReducer, createAppModalState } from "./appModalState";
import { useUserSession } from "./auth/UserSessionContext";
import { captureReferralFromLocation, cleanAuthAndReferralParams } from "./referrals/referralLink";
import ProfileWorkspace from "./profile/ProfileWorkspace";

library.add(
    fab,
    faCoffee,
    faArrowUpLong,
    faArrowUp,
    faCog,
    faSpinner,
    faQuoteLeft,
    faSquare,
    faCheckSquare
)

function App() {
  const [appModals, dispatchAppModal] = React.useReducer(
    appModalReducer,
    false,
    createAppModalState
  );
  const {
    currencyOpen: modalShow,
    sectionsOpen: adjustSections,
  } = appModals;
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [fetchesCount, setFetchesCount] = React.useState(0);
  const [settings, setSettings] = useState({});
  const [arrowClass, setArrowClass] = React.useState("");
  const [theme, setTheme] = useState(() => {
    const storedTheme = localStorage.getItem("theme");
    if (storedTheme === "dark" || storedTheme === "light") {
      return storedTheme;
    } else {
      localStorage.setItem("theme", "dark");
      return "dark";
    }
  });
  const [sortedSections, setSortedSections] = useState([]);
  const [sectionAttrsRecalc, setSectionAttrsRecalc] = useState(0);
  const [viewMode, setViewMode] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get("view");
    return ["history", "setups", "trades", "macro", "profile", "watchlist"].includes(requested) ? requested : "insights";
  });
  const [historyCollapseCommand, setHistoryCollapseCommand] = useState({
    collapsed: false,
    version: 0
  });
  const [showSectionTimings, setShowSectionTimings] = useState(false);
  const [adminExitState, setAdminExitState] = useState({ dirty: false, saving: false });
  const [macroNow, setMacroNow] = useState(Date.now());
  const { sessionStatus: adminSessionStatus, session: adminSession, setSession: setAdminSession } = useAdminSession(adminApi);
  const userSession = useUserSession();

  useEffect(() => {
      captureReferralFromLocation(window.location);
      initSettings();
      const authFields = Object.fromEntries(new URLSearchParams(window.location.search));
      if (authFields.id && authFields.auth_date && authFields.hash) {
          userSession.login(authFields, "WIDGET").catch(() => {});
          window.history.replaceState({}, document.title, cleanAuthAndReferralParams(window.location.href));
      }
      const tgMiniAppData = window.Telegram?.WebApp;
      if (tgMiniAppData && tgMiniAppData.initData) {
          userSession.login({ initData: tgMiniAppData.initData }, "MINI_APP").catch(() => {});
      }
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setMacroNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const syncViewFromLocation = () => {
      const requested = new URLSearchParams(window.location.search).get("view");
      if (["profile", "watchlist"].includes(viewMode) && requested !== viewMode && (adminExitState.saving
          || (adminExitState.dirty && !window.confirm("Discard unsaved administration changes?")))) {
        const restore = new URL(window.location.href);
        restore.searchParams.set("view", viewMode);
        window.history.pushState({}, "", restore);
        return;
      }
      setViewMode(["history", "setups", "trades", "macro", "profile", "watchlist"].includes(requested) ? requested : "insights");
    };
    window.addEventListener("popstate", syncViewFromLocation);
    return () => window.removeEventListener("popstate", syncViewFromLocation);
  }, [viewMode, adminExitState]);

  useEffect(() => {
    if (!adminExitState.dirty && !adminExitState.saving) return undefined;
    const warnBeforeUnload = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [adminExitState]);

  useEffect(() => {
    const display = userSession.snapshot?.settings?.preferences?.display;
    if (display && typeof display === "object") setSettings(display);
  }, [userSession.snapshot]);

  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
  };

  const navigateToProfile = () => {
    if (viewMode === "profile") return;
    if (adminExitState.saving || (adminExitState.dirty && !window.confirm("Discard unsaved watchlist changes?"))) return;
    const url = new URL(window.location.href);
    url.searchParams.set("view", "profile");
    window.history.pushState({}, "", url);
    setViewMode("profile");
  };

  const navigateToTab = (view) => {
    if (view === viewMode) return;
    if (adminExitState.saving || (adminExitState.dirty && !window.confirm("Discard unsaved watchlist changes?"))) return;
    const url = new URL(window.location.href);
    url.searchParams.set("view", view);
    window.history.pushState({}, "", url);
    setViewMode(view);
  };

  const navigateToInsights = () => {
    if (adminExitState.saving || (adminExitState.dirty
        && !window.confirm("Discard unsaved administration changes?"))) return false;
    const url = new URL(window.location.href);
    url.searchParams.delete("view");
    window.history.pushState({}, "", url);
    setViewMode("insights");
    return true;
  };

  const getQueryParam = (param) => {
    return new URLSearchParams(window.location.search).get(param);
  }

  const handleScroll = (e) => {
    setArrowClass("w-toplink pos_right" + (window.scrollY > 0 ? " active" : ""));
  };

  const initSettings = () => {

    localStorage.removeItem("chatId");
    window.addEventListener("scroll", handleScroll);

    let currency = getQueryParam("currency");

    if (currency) {
      currency = currency.toUpperCase();
      openPopup({
        index: 0,
        sectionEvents: [{
          bear: false,
          botActions: [],
          cur: currency,
          desc: "",
          name: currency,
          tf: "_15M",
          ts: null
        }],
        get currencyInfo() {
            return this.sectionEvents[this.index];
        }
      });
    }
  }

  const options = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false
  };

  let [filters, setFilters] = useState({});
  let [allCurrencies, setAllCurrencies] = React.useState([]);

  const setCookie = (key, value) => {
    Cookies.set(key, value);
  }

  const handleSetFilters = (fil) => {

    if (fil && Object.keys(fil).length !== 0) {
      setFilters(fil);
      localStorage.setItem("filters", JSON.stringify(fil));
    }

    console.log(JSON.stringify(fil));
  };

  const handleSetSettings = (sett) => {
    setSettings(sett);
    if (userSession.status === "authenticated") userSession.submitCommand({
      type: "PREFERENCE_UPDATE",
      expectedSnapshotRevision: userSession.snapshot?.revision,
      payload: {
        theme: userSession.snapshot?.settings?.preferences?.theme || "system",
        sectionOrder: userSession.snapshot?.settings?.preferences?.sectionOrder || [],
        display: sett
      }
    }).catch(() => {});
    setSortedSections([...getSortedSections(insights.sections, sett)]);
    setSectionAttrsRecalc(sectionAttrsRecalc + 1);
  }

  const selectedCurrencies = useMemo(
    () => (filters.currencies || []).map((currency) => currency.value),
    [filters.currencies]
  );

  const fetcher = async (...args) => {
    const res = await fetch(...args, { cache: "no-store" });
    setFetchesCount((count) => count + 1);
    if (!res.ok) {
      const message = await res.text();
      throw new Error(message || `Request failed with status ${res.status}`);
    }
    return res.json();
  };

  const {
    data: insights,
    error,
    mutate,
  } = useSWR(
    viewMode === "profile" ? null : `${ORIGIN}/insights?key=${KEY}`,
    fetcher,
    { refreshInterval: isPopupVisible ? 0 : 10000 }
  );

  const {
    data: macroSnapshot,
    error: macroError,
    mutate: mutateMacro,
  } = useSWR(
    viewMode === "profile" ? null : `${ORIGIN}/insights/macro-events?key=${KEY}`,
    fetcher,
    { refreshInterval: 6 * 60 * 1000, errorRetryInterval: 60 * 1000 }
  );

  const {
    data: sentimentSnapshot,
    error: sentimentError,
    mutate: mutateSentiment,
  } = useSWR(
    viewMode === "profile" ? null : `${ORIGIN}/insights/sentiment?key=${encodeURIComponent(KEY)}`,
    fetcher,
    { refreshInterval: 60 * 1000, errorRetryInterval: 60 * 1000 }
  );

  const macroCount = useMemo(
    () => getUpcomingMacroCount(macroSnapshot?.events || [], macroNow),
    [macroSnapshot, macroNow]
  );
  const setupCount = (insights?.setups?.setups || []).filter((setup) =>
    isFuturesSetup(setup) && (setup.status === "ARMED" || setup.status === "CONFIRMED")).length;

  useEffect(() => {

    if (insights) {

      setLastUpdated(new Date());

      const allCurr = {};

      insights.sections.forEach((section) => {

        if (!section.events) {
          return;
        }

        section.events.forEach((event, eventIndex) => {

          if (insights.currencyInfo[event.cur]) {
            if (!insights.currencyInfo[event.cur]?.appearances) {
              insights.currencyInfo[event.cur].appearances = 0;
            }
            insights.currencyInfo[event.cur].appearances++;
          }

          if (!allCurr[event.cur]) {
            allCurr[event.cur] = { label: event.cur, value: event.cur };
          }
        })
      })

      const allCurrOptions = [];

      for (const [key, value] of Object.entries(allCurr)) {
        allCurrOptions.push(value);
      }

      setAllCurrencies(allCurrOptions);

      setSortedSections([...getSortedSections(insights.sections, settings)]);
      setSectionAttrsRecalc(sectionAttrsRecalc + 1);
    }
  }, [insights]);

  const handleRefresh = async () => {
    await mutate();
    mutateMacro();
    mutateSentiment();
    setLastUpdated(Date.now()); // Оновлення часу вручну
  };

  const openPopup = useCallback((currencyData) => {
    const selectedEvent = currencyData?.sectionEvents?.[currencyData?.index];
    if (isAssessmentStar(selectedEvent)) {
      setIsPopupVisible(false);
      setSelectedCurrency(null);
      dispatchAppModal({ type: "CLOSE_CURRENCY" });
      const url = new URL(window.location.href);
      url.searchParams.set("view", "history");
      url.searchParams.set("historyView", "ai-assessments");
      url.searchParams.set("assessment", selectedEvent.assessmentId);
      window.history.replaceState({}, "", url);
      setViewMode("history");
      return;
    }
    setSelectedCurrency(currencyData);
    setIsPopupVisible(true);
    dispatchAppModal({ type: "OPEN_CURRENCY" });
  }, []);

  const openSettingsModal = () => {
    navigateToProfile();
  }

  const setSelectedCurrencyWrap = (data) => {
      //setSelectedCurrency((prev) => ({ ...prev, ...data }));
      setSelectedCurrency({...data});
  }

  const getFavourites = () => {

    const favs = [];

    for (const [key, value] of Object.entries(settings?.favourites || {})) {
      const currentEvent = insights?.sections
          ?.flatMap((section) => section.events || [])
          ?.find((event) => event.cur === key);
      favs.push({
        cur: key,
        name: key,
        assetType: value.assetType || currentEvent?.assetType,
        tf: "_15M",
        comment: value.comment,
        type: value.type
      });
    }

    return favs;
  }

  const getFavsSection = () => {
    return {
      desc: "FAVOURITES",
      title: "FAVOURITES",
      favourites: getFavourites()
    };
  }

  const isFavs = () => {
    return settings?.favourites && Object.keys(settings.favourites).length > 0;
  }

  const getStarred = () => {
    const list = [];
    for (const [key, value] of Object.entries(settings?.starred || {})) {
        list.push(value);
      // list.push({
      //   cur: key,
      //   name: key,
      //   tf: value?.tf || "_15M",
      //   comment: value?.comment,
      //   type: value?.type
      // });
    }
    return list;
  }

  const getStarredSection = () => {
    return {
      desc: "STARRED",
      title: "STARRED",
      starred: getStarred()
    };
  }

  const isStarred = () => {
    return settings?.starred && Object.keys(settings.starred).length > 0;
  }

  const insightSectionTitles = [
    ...(isFavs() ? ["FAVOURITES"] : []),
    ...(isStarred() ? ["STARRED"] : []),
    ...(sortedSections || []).map((section) => section.title).filter(Boolean)
  ];
  const uniqueInsightSectionTitles = [...new Set(insightSectionTitles)];
  const insightsAllCollapsed = uniqueInsightSectionTitles.length > 0
      && uniqueInsightSectionTitles.every(
          (title) => settings?.sectionSettings?.[title]?.isCollapsed === true
      );

  const toggleAllSections = () => {
    if (viewMode === "history") {
      setHistoryCollapseCommand((current) => ({
        collapsed: !current.collapsed,
        version: current.version + 1
      }));
      return;
    }
    if (viewMode !== "insights" || uniqueInsightSectionTitles.length === 0) {
      return;
    }

    const nextCollapsed = !insightsAllCollapsed;
    const sectionSettings = { ...(settings?.sectionSettings || {}) };
    uniqueInsightSectionTitles.forEach((title) => {
      sectionSettings[title] = {
        ...(sectionSettings[title] || {}),
        isCollapsed: nextCollapsed
      };
    });
    handleSetSettings({
      ...settings,
      sectionSettings
    });
  };

  const closePopup = () => {
    setIsPopupVisible(false);
    setSelectedCurrency(null);
  };

  const closeCurrencyModal = () => {
    dispatchAppModal({ type: "CLOSE_CURRENCY" });
    const url = new URL(window.location);
    url.search = '';
    window.history.replaceState({}, document.title, url);
  }

  if (viewMode === "profile") return (
    <div className={theme === "dark" ? "dark-mode" : "light-mode"}>
      <div id="main" className="profile-page-main">
        <CryptemberHeader isProfile theme={theme} toggleTheme={toggleTheme} onBackToInsights={navigateToInsights}/>
        <div className="profile-header-spacer" />
        <ProfileWorkspace
          onOpenSectionOrder={() => {
            if (navigateToInsights()) dispatchAppModal({ type: "OPEN_SECTIONS" });
          }}
          adminPanel={adminSessionStatus === "confirmed" ? (
            <AdminPanel api={adminApi} initialSession={adminSession} onSessionChange={setAdminSession}
              theme={theme} openPopup={openPopup} onGuardChange={setAdminExitState} embedded />
          ) : <p role="status">Loading administration…</p>}
        />
        {modalShow && <CurrencyPopup
          show={modalShow} onHide={() => {
            setIsPopupVisible(false);
            setSelectedCurrency(null);
            dispatchAppModal({ type: "CLOSE_CURRENCY" });
          }}
          selectedCurrency={selectedCurrency} setSelectedCurrency={setSelectedCurrencyWrap}
          settings={settings} insights={insights || { currencyInfo: {} }} setSettings={handleSetSettings}/>}
      </div>
      <CryptemberFooter />
    </div>
  );

  if (error || !insights) return <div className={theme === "dark" ? "dark-mode" : "light-mode"}>
    <div id="main"><CryptemberHeader openSettingsModal={openSettingsModal} theme={theme} toggleTheme={toggleTheme}/>
      <div style={{ height: "100px", width: "100%" }}/><p role="status">{error ? "Market data could not be loaded. Profile and activity remain available." : "Loading market data…"}</p>
    </div></div>;

  const  lastUpdatedTs = insights?.ts || null;
  const sectionTimings = insights?.sectionTimings || [];
  const slowSectionTimings = [...sectionTimings]
      .sort((a, b) => (b?.durationMs || 0) - (a?.durationMs || 0))
      .slice(0, 24);

  return (
      <div className={theme === "dark" ? "dark-mode" : "light-mode"}>
        <div id="main">
          <CryptemberHeader openSettingsModal={openSettingsModal} theme={theme} toggleTheme={toggleTheme}/>
          <div style={{"height": "100px", "width": "100%"}}></div>

          {/*Fetches: {fetchesCount}*/}
          <TopSection
              allCurrencies={allCurrencies}
              lastUpdatedTs={lastUpdatedTs}
              handleRefresh={handleRefresh}
              sendFiltersToParent={handleSetFilters}
              // setChatId={handleSetChatId}
              theme={theme}
              toggleTheme={toggleTheme}
              direction={insights.globalDirection}
              sentimentSnapshot={sentimentSnapshot}
              sentimentError={sentimentError}
              viewMode={viewMode}
          />
          <div className="view-tabs">
            <button
              type="button"
              onClick={() => navigateToTab("insights")}
              className={viewMode === "insights" ? "view-tab view-tab-active" : "view-tab"}
            >
              Insights
            </button>
            <button
              type="button"
              onClick={() => navigateToTab("setups")}
              className={viewMode === "setups" ? "view-tab view-tab-active" : "view-tab"}
            >
              {setupCount > 0 ? `Setups (${setupCount})` : "Setups"}
            </button>
            <button
              type="button"
              onClick={() => navigateToTab("history")}
              className={viewMode === "history" ? "view-tab view-tab-active" : "view-tab"}
            >
              History
            </button>
            <button
              type="button"
              onClick={() => navigateToTab("trades")}
              className={viewMode === "trades" ? "view-tab view-tab-active" : "view-tab"}
            >
              My Trades
            </button>
            <button
              type="button"
              onClick={() => navigateToTab("macro")}
              className={viewMode === "macro" ? "view-tab view-tab-active" : "view-tab"}
            >
              {macroCount > 0 ? `Macro (${macroCount})` : "Macro"}
            </button>
            <button type="button" onClick={() => navigateToTab("watchlist")}
              className={viewMode === "watchlist" ? "view-tab view-tab-active" : "view-tab"}>Watchlist</button>
            {(viewMode === "insights" || viewMode === "history") && (
              <button
                type="button"
                className="collapseAllSectionsButton"
                onClick={toggleAllSections}
                title={
                  (viewMode === "history" ? historyCollapseCommand.collapsed : insightsAllCollapsed)
                    ? "Expand all sections"
                    : "Collapse all sections"
                }
                aria-label={
                  (viewMode === "history" ? historyCollapseCommand.collapsed : insightsAllCollapsed)
                    ? "Expand all sections"
                    : "Collapse all sections"
                }
              >
                {(viewMode === "history" ? historyCollapseCommand.collapsed : insightsAllCollapsed)
                  ? <FiChevronsDown aria-hidden="true" />
                  : <FiChevronsUp aria-hidden="true" />}
              </button>
            )}
            {viewMode === "insights" && sectionTimings.length > 0 && (
              <button
                type="button"
                className={showSectionTimings
                  ? "sectionTimingsToggle sectionTimingsToggleActive"
                  : "sectionTimingsToggle"}
                onClick={() => setShowSectionTimings((visible) => !visible)}
                title={showSectionTimings ? "Hide collection timings" : "Show collection timings"}
                aria-label={showSectionTimings ? "Hide collection timings" : "Show collection timings"}
                aria-expanded={showSectionTimings}
              >
                <FiActivity aria-hidden="true" />
              </button>
            )}
          </div>
          {viewMode === "insights" && showSectionTimings && sectionTimings.length > 0 && (
            <div className="sectionTimingsPanel" title="Insights collection timing by backend section">
              <div className="sectionTimingsHeader">
                Collection {insights.totalCollectionMs ? `${(insights.totalCollectionMs / 1000).toFixed(1)}s` : ""}
              </div>
              <div className="sectionTimingsList">
                {slowSectionTimings.map((timing, index) => (
                  <span
                    key={`${timing.name}-${index}`}
                    className={timing.successful === false ? "sectionTiming sectionTimingFailed" : "sectionTiming"}
                    title={`${timing.name}: ${timing.durationMs}ms, +${timing.addedSections || 0} sections, +${timing.addedEvents || 0} events`}
                  >
                    {timing.name} {(timing.durationMs / 1000).toFixed(1)}s
                    {timing.successful === false ? " failed" : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className={viewMode === "macro" ? "wrapper macroWrapper" : viewMode === "watchlist" ? "wrapper watchlistWrapper" : viewMode === "trades" ? "wrapper tradesWrapper" : "wrapper"}>
            {viewMode === "insights" && (
              <>
                {isFavs() ? (
                  <div>
                    <Section
                      key="favourites"
                      section={getFavsSection()}
                      openPopup={openPopup}
                      insights={insights}
                      filters={filters}
                      currencies={selectedCurrencies}
                      settings={settings}
                      setSettings={handleSetSettings}
                    ></Section>
                  </div>
                ) : null}
                {isStarred() ? (
                  <div>
                    <Section
                      key="starred"
                      section={getStarredSection()}
                      openPopup={openPopup}
                      insights={insights}
                      filters={filters}
                      currencies={selectedCurrencies}
                      settings={settings}
                      setSettings={handleSetSettings}
                    ></Section>
                  </div>
                ) : null}

                {sortedSections && sortedSections.length > 0 ? (
                  sortedSections.map((section, eventIndex) => (
                    <Section
                      key={eventIndex}
                      section={section}
                      filters={filters}
                      reloadAttrs={sectionAttrsRecalc}
                      openPopup={openPopup}
                      insights={insights}
                      currencies={selectedCurrencies}
                      settings={settings}
                      setSettings={handleSetSettings}
                    />
                  ))
                ) : (
                  <div>No events there...</div>
                )}
              </>
            )}

            {viewMode === "history" && (
              <HistoryTab
                theme={theme}
                insights={insights}
                filters={filters}
                openPopup={openPopup}
                collapseCommand={historyCollapseCommand}
                settings={settings}
                setSettings={handleSetSettings}
              />
            )}

            {viewMode === "setups" && (
              <SetupsTab
                snapshot={insights.setups}
                filters={filters}
                openPopup={openPopup}
              />
            )}

            {viewMode === "trades" && (
              <MyTradesTab
                openPopup={openPopup}
              />
            )}

            {viewMode === "watchlist" && <DiscoveryWatchlistTab openPopup={openPopup} api={adminApi}
              canMaintain={adminSessionStatus === "confirmed" && adminSession?.authenticated === true} onGuardChange={setAdminExitState} onOpenProfile={navigateToProfile} />}

            {viewMode === "macro" && (
              <>
                <MacroEventsTab
                  snapshot={macroSnapshot}
                  error={macroError}
                  isLoading={!macroSnapshot && !macroError}
                  now={macroNow}
                />
                <SentimentTab />
              </>
            )}

          </div>
          <>
              <CurrencyPopup
                  show={modalShow}
                  onHide={() => closeCurrencyModal()}
                  selectedCurrency={selectedCurrency}
                  setSelectedCurrency={setSelectedCurrencyWrap}
                  settings={settings}
                  insights={insights}
                  setSettings={handleSetSettings}/>

              <SectionsConfigurationModal
                  show={adjustSections}
                  sections={sortedSections}
                  settings={settings}
                  setSettings={handleSetSettings}
                  onHide={() => dispatchAppModal({ type: "CLOSE_SECTIONS" })}
              />
            </>
        </div>
        <CryptemberFooter/>

        <a className={arrowClass}
           href="#"
           title="Back to top"
           aria-hidden="true">
          <FontAwesomeIcon icon={faArrowUp} className="fa-bold"/>
        </a>

      </div>
  );
}

export default App;
